import type { DeviceMicroconfiguration, DeviceParameterInput } from '@cobliteam/common-types';
import type { Configuration, IRequest } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { GetDeviceMicroconfigurationAPI } from './definitions';
import { toDeviceMicroconfiguration } from './microconfigurationTransformer';

/**
 * Único ponto de acesso do dashboard à API de microconfiguração da Betoneira.
 *
 * Concentrar tudo aqui é deliberado: essa API pública entra em *deprecation path* (o DD "Perfil de
 * Configurações" move a leitura para `/v2`, read-only, com vocabulário abstrato), e a migração deve
 * ficar localizada em um arquivo. Ver docs/adr/0016-avisos-sonoros-sem-heranca-e-provisorio.md.
 */
export class MicroconfigurationRequester {
  private requester: Requester;
  private readonly baseUrl = 'devices-configuration';

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  /**
   * Configuração de todos os dispositivos da frota, com `target` e `actual`. Uma chamada resolve
   * todos os campos de dispositivo da página, incluindo o status de sincronização (que é o diff
   * entre as duas metades).
   */
  public getFleetMicroconfiguration = (): Promise<DeviceMicroconfiguration[]> => {
    const requestData: IRequest = {
      endpoint: this.baseUrl,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => {
      // O contrato da rota é uma lista, mas o cast cego transformaria qualquer outro shape
      // (envelope de erro, resposta paginada) num TypeError de `.map` no meio do render, em vez de
      // um erro de query que a UI sabe tratar.
      if (!Array.isArray(response.data)) {
        throw new Error('devices-configuration: esperava uma lista de dispositivos');
      }

      return (response.data as GetDeviceMicroconfigurationAPI[]).map(toDeviceMicroconfiguration);
    });
  };

  /**
   * Grava parâmetros em **um** dispositivo e dispara a reconciliação imediata.
   *
   * Uma chamada por dispositivo, e não o `PATCH /bulk`: o endpoint em lote não é `@Transactional` e
   * lança na primeira falha depois de já ter gravado os anteriores, o que impossibilita o relatório
   * de falha parcial por placa.
   */
  public updateDeviceParameters = (deviceId: string, parameters: DeviceParameterInput[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `${this.baseUrl}/${deviceId}`,
      method: HttpMethod.PATCH,
      body: JSON.stringify({ parameters }),
    };

    return this.requester.request(requestData).then(() => undefined);
  };
}

export default MicroconfigurationRequester;
